import { proposalQueryKeys } from "@platform/mpx-quotes-grid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";

import type { ProposalPlanLevelFields } from "./useMaterializeProposalMutation";
import type { RfpBenefitPlan } from "@platform/btx-plan-designer";
import type { PadFillTarget } from "@platform/mpx-quotes-grid";
import type { AxiosInstance } from "axios";

/** Request body for POST /btx/v1/proposal-plan-groups/:id/plans. */
export interface AddPlanToGroupRequest extends ProposalPlanLevelFields {
  position: number;
  // Optional: a plan-level-only edit (e.g. funding type) carries no benefit values.
  benefits?: RfpBenefitPlan["benefits"];
  // Plan type — carried on a paste so the pasted plan keeps the source's product type.
  type?: RfpBenefitPlan["type"];
}

const padFillKey = (target: PadFillTarget): string =>
  `${target.proposalPlanGroupId}:${target.planPosition}`;

const postAddPlanToGroup = async (
  axiosInstance: AxiosInstance,
  target: PadFillTarget,
  benefits: RfpBenefitPlan["benefits"],
  planFields: ProposalPlanLevelFields,
): Promise<string> => {
  const body: AddPlanToGroupRequest = {
    position: target.planPosition,
    benefits,
    ...planFields,
  };
  const response = await axiosInstance.post<{ id: string }>(
    `btx/v1/proposal-plan-groups/${target.proposalPlanGroupId}/plans`,
    body,
  );
  if (!response.data.id) {
    throw new Error(
      `Add-plan response for group ${target.proposalPlanGroupId} did not include a plan id`,
    );
  }
  return response.data.id;
};

interface AddPlanVars {
  target: PadFillTarget;
  benefits: RfpBenefitPlan["benefits"];
  planFields: ProposalPlanLevelFields;
  /** Invoked with the newly created plan's real id once it's added to the group. */
  onAdded?: (planId: string) => void;
}

interface UseAddPlanToGroupMutationOptions {
  rfpId: string;
  axiosInstance: AxiosInstance;
  onSaveError?: (err: Error) => void;
}

/**
 * Fills an empty pad slot on a real under-quoted plan group with a real plan
 * carrying the analyst's edit (CD-1612 partial fill).
 *
 * The add-plan endpoint always creates a new plan (it is not idempotent per
 * position), so we guard against firing twice for the same (group, position):
 * the key stays in flight until the proposals refetch lands and the pad column
 * has been replaced by the real plan. Without this, a second edit during the
 * refetch window would create a duplicate plan.
 */
export const useAddPlanToGroupMutation = ({
  rfpId,
  axiosInstance,
  onSaveError,
}: UseAddPlanToGroupMutationOptions): {
  addPlanToGroup: (
    target: PadFillTarget,
    benefits: RfpBenefitPlan["benefits"],
    planFields: ProposalPlanLevelFields,
    onAdded?: (planId: string) => void,
  ) => void;
} => {
  const queryClient = useQueryClient();
  const queryKey = proposalQueryKeys.all(rfpId);
  const inFlightKeys = useRef(new Set<string>());

  const { mutate } = useMutation({
    mutationFn: ({ target, benefits, planFields }: AddPlanVars) =>
      postAddPlanToGroup(axiosInstance, target, benefits, planFields),
    onSuccess: (planId, { onAdded }: AddPlanVars) => {
      onAdded?.(planId);
      // Await the refetch so the pad slot is replaced before the in-flight
      // guard is released (see onSettled) — otherwise a quick second edit
      // would create a duplicate plan.
      return queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: Error) => {
      onSaveError?.(err);
    },
    onSettled: (_data, _err, { target }: AddPlanVars) => {
      inFlightKeys.current.delete(padFillKey(target));
    },
  });

  const addPlanToGroup = useCallback(
    (
      target: PadFillTarget,
      benefits: RfpBenefitPlan["benefits"],
      planFields: ProposalPlanLevelFields,
      onAdded?: (planId: string) => void,
    ) => {
      const key = padFillKey(target);
      if (inFlightKeys.current.has(key)) {
        // An add-plan for this (group, position) is already in flight, so this
        // call's onAdded/retry would otherwise be silently dropped.
        onSaveError?.(
          new Error(
            `Add-plan already in flight for group ${target.proposalPlanGroupId}`,
          ),
        );
        return;
      }
      inFlightKeys.current.add(key);
      mutate({ target, benefits, planFields, onAdded });
    },
    [mutate, onSaveError],
  );

  return { addPlanToGroup };
};
